import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ShieldCheck, AlertTriangle, CheckCircle, XCircle, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Credential {
  id: string;
  surgeonName: string;
  npi: string;
  npiVerified: boolean;
  taxId: string;
  taxIdVerified: boolean;
  payers: { name: string; status: 'active' | 'expiring' | 'expired'; expiresAt: string }[];
}

const mockCredentials: Credential[] = [
  {
    id: '1', surgeonName: 'Dr. Peter Larkin', npi: '1234567890', npiVerified: true, taxId: '**-***4321', taxIdVerified: true,
    payers: [
      { name: 'BCBS Ohio', status: 'active', expiresAt: '2027-03-15' },
      { name: 'UnitedHealthcare', status: 'active', expiresAt: '2026-11-01' },
      { name: 'Aetna', status: 'expiring', expiresAt: '2026-04-15' },
    ],
  },
  {
    id: '2', surgeonName: 'Dr. Sarah Chen', npi: '9876543210', npiVerified: true, taxId: '**-***8765', taxIdVerified: true,
    payers: [
      { name: 'BCBS Ohio', status: 'active', expiresAt: '2027-01-20' },
      { name: 'UnitedHealthcare', status: 'expired', expiresAt: '2026-01-10' },
    ],
  },
  {
    id: '3', surgeonName: 'Dr. Michael Torres', npi: '5555512345', npiVerified: false, taxId: '**-***9999', taxIdVerified: true,
    payers: [
      { name: 'BWC Ohio', status: 'active', expiresAt: '2026-12-31' },
    ],
  },
  {
    id: '4', surgeonName: 'Dr. Amanda Wright', npi: '1112223334', npiVerified: true, taxId: '**-***2222', taxIdVerified: false,
    payers: [
      { name: 'BCBS Ohio', status: 'expiring', expiresAt: '2026-03-01' },
      { name: 'Aetna', status: 'expired', expiresAt: '2025-12-15' },
    ],
  },
];

function StatusIcon({ ok }: { ok: boolean }) {
  return ok
    ? <CheckCircle className="w-4 h-4 text-emerald-500" />
    : <XCircle className="w-4 h-4 text-destructive" />;
}

function PayerBadge({ status }: { status: 'active' | 'expiring' | 'expired' }) {
  if (status === 'active') return <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 text-[10px]">Active</Badge>;
  if (status === 'expiring') return <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-[10px]">Expiring</Badge>;
  return <Badge variant="destructive" className="text-[10px]">Expired</Badge>;
}

export function CredentialingVault() {
  const expiredCount = mockCredentials.reduce((n, c) => n + c.payers.filter(p => p.status === 'expired').length, 0);

  return (
    <div className="space-y-4">
      {expiredCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{expiredCount} expired credential{expiredCount > 1 ? 's' : ''} detected.</strong> LINK will block case submission for affected surgeon–payer combinations until renewed.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Credentialing Vault
          </CardTitle>
          <CardDescription className="flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            Encrypted storage — auto-verified against NPPES & payer portals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Surgeon</TableHead>
                <TableHead className="text-center">NPI</TableHead>
                <TableHead className="text-center">Tax ID</TableHead>
                <TableHead>Payer Credentialing</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockCredentials.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.surgeonName}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1.5">
                      <code className="text-xs text-muted-foreground">{c.npi}</code>
                      <StatusIcon ok={c.npiVerified} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1.5">
                      <code className="text-xs text-muted-foreground">{c.taxId}</code>
                      <StatusIcon ok={c.taxIdVerified} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {c.payers.map((p, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="text-xs">{p.name}</span>
                          <PayerBadge status={p.status} />
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
